/**
 * Web component: Campaign teaser (campaign-teaser)
 * -----------------------------------------------------------------------------
 * Campaign Teaser.
 *
 */
import classes from '~/config/classes';
import './campaign-teaser.scss';

import { vueReady } from '~/core/runners';

import { dateTime } from '~/helpers/general';

import {
  checkIfActiveNonPrivate,
  handleTrackingCampaign,
  updateTeaser,
} from '~/helpers/teasers';

import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useIndexStore from '~stores/index';
import useOverlaysStore from '~stores/overlays';

let cartStore;
let discountCampaignsStore;
let indexStore;
let overlaysStore;

/**
 * DOM selectors.
 */
const selectors = {
  campaignContainer: '[js-campaign-teaser="content"]',
  conditions: '[js-campaign-teaser="conditions"]',
  copyButton: '[js-campaign-teaser="copyButton"]',
  countdownLive: '[js-campaign-teaser="countdownLive"]',
  countdownTransition: '[js-campaign-teaser="countdownTransition"]',
  hintClose: '[js-campaign-teaser="hintClose"]',
  hintDialog: '[js-campaign-teaser="hintDialog"]',
  hintOverlay: '[js-campaign-teaser="hintOverlay"]',
  hintToggle: '[js-campaign-teaser="hintToggle"]',
  live: '[js-campaign-teaser="live"]',
  notificationText: '[js-data-notification="overlaytext"]',
  promoText: '[js-campaign-teaser="promoText"]',
  transition: '[js-campaign-teaser="transition"]',
};

/**
 * Create new <campaign-teaser> custom element.
 */
class CampaignTeaser extends HTMLElement {
  data(component) {
    const campaignContainer = component.querySelector(
      selectors.campaignContainer,
    );
    if (campaignContainer) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleTrackingCampaign(
              campaignContainer,
              'Campaign Teaser',
              'view_promotion',
            );
            observer.disconnect();
          }
        });
      });

      observer.observe(campaignContainer);
    }

    return {
      selectors: {
        campaignContainer,
        conditions: component.querySelector(selectors.conditions),
        copyButton: component.querySelector(selectors.copyButton),
        countdownLive: component.querySelector(selectors.countdownLive),
        countdownTransition: component.querySelector(
          selectors.countdownTransition,
        ),
        hintClose: component.querySelector(selectors.hintClose),
        hintDialog: component.querySelector(selectors.hintDialog),
        hintOverlay: component.querySelector(selectors.hintOverlay),
        hintToggle: component.querySelector(selectors.hintToggle),
        live: component.querySelector(selectors.live),
        promoText: component.querySelector(selectors.promoText),
        notificationText: document.querySelector(selectors.notificationText),
        transition: component.querySelector(selectors.transition),
      },
    };
  }

  constructor() {
    super();
    this.$data = this.data(this);
    this.teaserUpdated = false;
    // Last applied code rendered to the DOM. `null` (not `''`) so the first
    // sync always runs, even when the computed state is "not applied" and we
    // need to reconcile against the server-rendered markup.
    this.appliedCode = null;
    this.hintOpen = false;

    this.onHintKeydown = this.onHintKeydown.bind(this);
    this.onHintPointerDown = this.onHintPointerDown.bind(this);
    this.onHintFocusOut = this.onHintFocusOut.bind(this);
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { conditions, copyButton, hintClose, hintToggle } =
      this.$data.selectors;

    if (copyButton) {
      copyButton.addEventListener('click', (event) => {
        this.applyCode(event);
      });
    }

    // The check-mark list styling removes the native markers, and Safari/
    // VoiceOver then stops announcing the <ul> as a list. The rich text comes
    // from Shopify, so the role can't be set in the Liquid.
    conditions?.querySelectorAll('ul').forEach((list) => {
      list.setAttribute('role', 'list');
    });

    hintToggle?.addEventListener('click', () => {
      this.openHint();
    });

    hintClose?.addEventListener('click', () => {
      this.closeHint({ restoreFocus: true });
    });

    // Initialise the button state and subscribe to store changes. The state is
    // derived from the active campaign OR the code being applied in the cart, so
    // it survives a page refresh (the cart store persists its response, whereas
    // the discount-campaigns `active` is repopulated asynchronously on hydration).
    this.syncTeaserState();

    if (discountCampaignsStore) {
      this.discountUnsubscribe = discountCampaignsStore.$subscribe(() => {
        this.syncTeaserState();
      });
    }

    if (cartStore) {
      this.cartUnsubscribe = cartStore.$subscribe(() => {
        this.syncTeaserState();
      });
    }

    this.handleCountdown();
  }

  /**
   * Check if this teaser's code is currently applied to the cart.
   * - Primary signal: the code is present and applicable in the cart's
   *   `discount_codes` (the reliable value that survives a page refresh).
   * - Fallback: the active-campaign cart attribute ("code:type"), mirroring the
   *   cart store's getCartAttributeCode() and the section's server-side render.
   * NOTE: the Liquid server render checks the same intent via
   * `cart.discount_applications.title` (the only applied-discount list available
   * at render time). For code-based discounts `title` equals the code, so the
   * two stay in sync; an automatic discount whose title collides with a campaign
   * code is the one edge case where they could diverge.
   * @returns {String} The applied code, or empty string if not applied.
   */
  getAppliedCartCode() {
    const code = this.$data.selectors.copyButton?.dataset?.code;

    if (!code || !cartStore) {
      return '';
    }

    const upperCode = code.toUpperCase();

    const isAppliedInCart = (cartStore.response.discount_codes || []).some(
      (discount) =>
        discount.code.toUpperCase() === upperCode &&
        discount.applicable === true,
    );

    if (isAppliedInCart) {
      return code;
    }

    const cartCampaignCode = (
      cartStore.response.attributes?.discount_codes || ''
    ).split(':')[0];

    return cartCampaignCode && cartCampaignCode.toUpperCase() === upperCode
      ? code
      : '';
  }

  /**
   * Sync the teaser button/promo to the applied or default state.
   * - Applied when there is an active non-private campaign OR the teaser's code
   *   is applied in the cart.
   */
  syncTeaserState() {
    const active = discountCampaignsStore?.active;
    const isActiveNonPrivate = checkIfActiveNonPrivate({ active });
    const cartCode = this.getAppliedCartCode();
    const appliedCode = isActiveNonPrivate ? active.code[0] : cartCode;

    // Not applied while still hydrating: don't flash the default ("redeem")
    // state over the correct server-rendered state. The cart and discount
    // stores haven't loaded yet, so a "not applied" result can't be trusted -
    // only reset once hydration is complete.
    if (!appliedCode && !indexStore?.hydrated) {
      return;
    }

    // Both store subscriptions fire on every mutation (e.g. each cart status
    // change), but the teaser only needs touching when the applied code itself
    // changes. Skip the redundant DOM work otherwise.
    if (appliedCode === this.appliedCode) {
      return;
    }

    this.appliedCode = appliedCode;

    // updateTeaser owns the icon visibility + `teaserUpdated` flag (its reset
    // branch is gated on that flag); updateText owns the promo/button visibility
    // classes and resets unconditionally. Both are needed to fully reconcile a
    // server-rendered state the JS never set the flag for.
    updateTeaser.call(this, {
      active: appliedCode ? { type: 'discount', code: [appliedCode] } : false,
    });
    this.updateText(appliedCode);
  }

  /**
   * Update the button/promo text for the applied or default state.
   * @param {String} appliedCode - The applied code, or empty string for default.
   */
  updateText(appliedCode) {
    const { copyButton, promoText } = this.$data.selectors;

    if (appliedCode) {
      // Code is applied - show promo text and button with the code text
      if (promoText) promoText.classList.remove(classes.hidden);
      if (copyButton) {
        copyButton.classList.remove(classes.hidden);
        const copyButtonText = copyButton.querySelector('span:first-child');
        if (copyButtonText) {
          copyButtonText.innerText = appliedCode;
        }
        copyButton.disabled = true;
      }
    } else {
      // Code is removed or private - show button, hide promo text
      if (promoText) promoText.classList.add(classes.hidden);
      if (copyButton) {
        copyButton.classList.remove(classes.hidden);
        const copyButtonText = copyButton.querySelector('span:first-child');
        if (copyButtonText && copyButton.dataset?.text) {
          copyButtonText.innerText = copyButton.dataset.text;
        }
        copyButton.disabled = false;
      }
    }
  }

  /**
   * Two timers, one visible countdown below the banner at any time:
   * - transition: from the pre-campaign countdown start until the campaign
   *   starts, then swap the banner and countdown over to the live phase.
   * - live: the end countdown, hidden again once the campaign has ended.
   */
  handleCountdown() {
    const createCountdown = (startTime, endTime, onExpire) => {
      let result = dateTime(startTime, endTime);

      if (result) {
        const countdownInterval = setInterval(() => {
          result = dateTime(startTime, endTime);
          if (!result) {
            onExpire();
            clearInterval(countdownInterval);
          }
        }, 1000);
      }
    };

    const { countdownLive, countdownTransition, live, transition } =
      this.$data.selectors;

    createCountdown(
      this.dataset.transitionStart,
      this.dataset.transitionEnd,
      () => {
        this.classList.remove('is-transition');
        transition?.classList.add(classes.hidden);
        countdownTransition?.classList.add(classes.hidden);
        live?.classList.remove(classes.hidden);
        countdownLive?.classList.remove(classes.hidden);
        cnvs.EventBus.emit('campaign:countdown-finished');
      },
    );

    createCountdown(
      Number(this.dataset.liveStart),
      this.dataset.liveEnd,
      () => {
        countdownLive?.classList.add(classes.hidden);
      },
    );
  }

  /**
   * Open the hint overlay and move focus into the dialog. It's non-modal, so
   * there is no focus trap: it closes on Escape, the close button, a click
   * outside or when focus leaves it.
   */
  openHint() {
    const { hintDialog, hintOverlay, hintToggle } = this.$data.selectors;

    if (this.hintOpen || !hintDialog || !hintOverlay) {
      return;
    }

    this.hintOpen = true;
    hintOverlay.classList.remove(classes.hidden);
    this.classList.add('is-hint-open');
    hintToggle?.setAttribute('aria-expanded', 'true');
    hintDialog.focus();

    document.addEventListener('keydown', this.onHintKeydown);
    document.addEventListener('pointerdown', this.onHintPointerDown);
    hintDialog.addEventListener('focusout', this.onHintFocusOut);
  }

  /**
   * Close the hint overlay.
   * @param {Object} options
   * @param {Boolean} options.restoreFocus - Return focus to the info icon.
   * Only for keyboard/close-button dismissal: after an outside click or tab-out
   * focus already went where the user wanted it.
   */
  closeHint({ restoreFocus = false } = {}) {
    const { hintOverlay, hintToggle } = this.$data.selectors;

    if (!this.hintOpen) {
      return;
    }

    this.hintOpen = false;
    this.removeHintListeners();
    hintOverlay?.classList.add(classes.hidden);
    this.classList.remove('is-hint-open');
    hintToggle?.setAttribute('aria-expanded', 'false');

    if (restoreFocus) {
      // The toggle is only visible again once `is-hint-open` is gone.
      hintToggle?.focus();
    }
  }

  removeHintListeners() {
    const { hintDialog } = this.$data.selectors;

    document.removeEventListener('keydown', this.onHintKeydown);
    document.removeEventListener('pointerdown', this.onHintPointerDown);
    hintDialog?.removeEventListener('focusout', this.onHintFocusOut);
  }

  onHintKeydown(event) {
    if (event.key === 'Escape') {
      this.closeHint({ restoreFocus: true });
    }
  }

  onHintPointerDown(event) {
    const { hintDialog } = this.$data.selectors;

    if (!hintDialog?.contains(event.target)) {
      this.closeHint();
    }
  }

  onHintFocusOut(event) {
    const { hintDialog } = this.$data.selectors;

    // A null relatedTarget is a click on non-focusable content or the window
    // losing focus; clicks are handled by the pointerdown listener.
    if (event.relatedTarget && !hintDialog.contains(event.relatedTarget)) {
      this.closeHint();
    }
  }

  /**
   * Component lifecycle - unmounted.
   */
  disconnectedCallback() {
    // Clean up store subscriptions
    if (this.discountUnsubscribe) {
      this.discountUnsubscribe();
    }

    if (this.cartUnsubscribe) {
      this.cartUnsubscribe();
    }

    this.removeHintListeners();
  }

  /**
   * Apply code from teaser button
   * @param {Event} event
   */
  applyCode(event) {
    // Get the code from the button's data attribute
    const code = event.currentTarget.dataset.code;

    if (!code) {
      return;
    }

    if (!discountCampaignsStore || !overlaysStore) {
      return;
    }

    const { copyButton, notificationText, promoText } = this.$data.selectors;

    const copyButtonText = copyButton.querySelector('span:first-child');

    discountCampaignsStore.setEnteredCode(code.toUpperCase());

    // Listen for code state changes to open overlay when ready
    const unsubscribe = discountCampaignsStore.$subscribe((mutation, state) => {
      if (state.codeState === 'loading') {
        if (copyButtonText)
          copyButtonText.innerText =
            cnvs.strings.accessibility.loading || 'Loading...';
      } else if (state.codeState === 'valid') {
        notificationText.innerHTML =
          cnvs.strings.discount_campaigns.notification_text;

        updateTeaser.call(this, {
          active: discountCampaignsStore.active,
        });

        // Reset code state after successful application
        discountCampaignsStore.setCodeState('ready');

        // Show the promo text and update button with the active code
        if (promoText) promoText.classList.remove(classes.hidden);
        if (copyButton) {
          copyButton.classList.remove(classes.hidden);
          copyButtonText.innerText = discountCampaignsStore.active.code[0];
          copyButton.disabled = true;
        }
        // Code was successfully applied, open the overlay
        overlaysStore.open({
          ignoreDismissed: true,
          namespace: 'discountCampaignsActivatedModal',
          clearQueue: true,
        });

        unsubscribe();
      } else if (state.codeState === 'invalid' || state.codeState === 'error') {
        cnvs.ReportError('Code application failed:', state.errorType);

        // Reset button text to original
        if (copyButton?.dataset?.text) {
          copyButtonText.innerText = copyButton.dataset.text;
        }

        unsubscribe();
      }
    });
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('campaign-teaser')) {
    return;
  }

  cartStore = useCartStore();
  discountCampaignsStore = useDiscountCampaignsStore();
  indexStore = useIndexStore();
  overlaysStore = useOverlaysStore();

  customElements.define('campaign-teaser', CampaignTeaser);
});
